import { createInstance, dateReviver } from '@/api';
import {
    EntityAttributeClient,
    EntityAttributeDetailDto,
    UpdateEntityAttributeDto,
    CreateEntityAttributeDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useEntityModelEditStore } from '../entity-model/entity-model-edit.store';
import { useCodeListAttributeMappingDetailStore } from '../code-list-attribute-mapping/code-list-attribute-mapping-detail.store';

export interface EntityAttributeStoreState {
    items?: { key: string; value: EntityAttributeDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new EntityAttributeClient('', createInstance(), dateReviver);

export const useEntityAttributeStore = defineStore('configuration-entity-attribute-store', {
    state: (): EntityAttributeStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byModel: (state) => {
            return (modelId: number): EntityAttributeDetailDto[] => {
                const key = `model-${modelId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async getByModel(modelId: number, reloading: boolean = false) {
            this.clear(modelId);
            this.loadDependencies(modelId);
            reloading ? (this.reloading = true) : (this.loading = true);

            const key = `model-${modelId}`;

            try {
                const response = await client.getByModel(modelId);
                if (response?.length) {
                    this.items = [...this.items, { key, value: response }];
                }
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entityAttribute.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(dto: CreateEntityAttributeDto) {
            try {
                this.reloading = true;
                await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('entityAttribute.detail.success.create.title'));
                await this.getByModel(dto.entityModelId, true);
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('entityAttribute.detail.error.create.title'),
                    error,
                    false
                );
            } finally {
                useEntityModelEditStore().closeCreate();
                this.reloading = false;
            }
        },
        async update(modelId: number, id: number, dto: UpdateEntityAttributeDto) {
            try {
                this.reloading = true;
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('entityAttribute.detail.success.update.title'));
                await this.getByModel(modelId, true);
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('entityAttribute.detail.error.update.title'),
                    error,
                    false
                );
            } finally {
                useEntityModelEditStore().closeEdit();
                this.reloading = false;
            }
        },
        async remove(modelId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('entityAttribute.detail.success.delete.title'));
                await this.getByModel(modelId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entityAttribute.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(modelId: number) {
            const key = `model-${modelId}`;

            this.items = this.items.filter((x) => x.key !== key);
        },
        loadDependencies(modelId: number) {
            try {
                useCodeListAttributeMappingDetailStore().get(modelId);
            } catch {}
        }
    }
});
